import {
  createAgentWorkspaceAnchor,
  type AgentWorkspaceAnchor,
  type AgentWorkspaceAnchorKind,
  type AgentWorkspaceTargetFormat,
} from "./anchor-model";

type ChatContextRecord = Record<string, unknown>;

interface AnchorKindRule {
  kind: AgentWorkspaceAnchorKind;
  tokens: string[];
  targetFormats: AgentWorkspaceTargetFormat[];
  evidenceRefs: string[];
  ruleIds?: string[];
}

const ANCHOR_KIND_RULES: AnchorKindRule[] = [
  {
    kind: "report.table-cell",
    tokens: ["cell", "header", "thead", "th"],
    targetFormats: ["preview", "pdf", "docx", "pptx", "hwpx"],
    evidenceRefs: [
      "apps/cogo-studio/src/features/report-workspace/report-workspace-model.ts",
    ],
    ruleIds: ["table.cell-insets", "table.text-wrap"],
  },
  {
    kind: "report.table-row",
    tokens: ["row", "tr"],
    targetFormats: ["preview", "pdf", "docx", "pptx", "hwpx"],
    evidenceRefs: [
      "apps/cogo-studio/src/features/report-workspace/report-workspace-model.ts",
    ],
    ruleIds: ["table.row-height"],
  },
  {
    kind: "report.table",
    tokens: ["table", "grid"],
    targetFormats: ["preview", "pdf", "docx", "pptx", "hwpx"],
    evidenceRefs: [
      "apps/cogo-studio/src/features/report-workspace/report-workspace-model.ts",
    ],
    ruleIds: ["table.conversion-profile", "table.border-priority"],
  },
  {
    kind: "report.chart",
    tokens: ["chart", "graph", "series"],
    targetFormats: ["preview", "pdf", "docx", "pptx", "hwpx", "xlsx"],
    evidenceRefs: [
      "apps/cogo-studio/src/features/report-workspace/chart-semantic-inspector.ts",
    ],
    ruleIds: ["chart.semantic-sidecar"],
  },
  {
    kind: "report.image",
    tokens: ["image", "picture", "figure"],
    targetFormats: ["preview", "pdf", "docx", "pptx", "hwpx"],
    evidenceRefs: [
      "apps/cogo-studio/src/features/report-workspace/report-workspace-model.ts",
    ],
    ruleIds: ["image.placement"],
  },
  {
    kind: "sheet.range",
    tokens: ["sheet", "range", "formula", "cellref", "workbook"],
    targetFormats: ["preview", "xlsx"],
    evidenceRefs: [
      "apps/cogo-studio/src/features/report-workspace/univer-formula-oracle.ts",
    ],
    ruleIds: ["sheet.formula-oracle"],
  },
  {
    kind: "report.document",
    tokens: ["report", "document", "docx", "pptx", "hwpx", "pdf"],
    targetFormats: ["preview", "pdf", "docx", "pptx", "hwpx"],
    evidenceRefs: ["docs/studio/cogo-agent-report-workspace-2026-07-04.md"],
    ruleIds: ["document.export-profile"],
  },
];

function readString(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value : null;
}

function readRecord(value: unknown): ChatContextRecord | null {
  return typeof value === "object" && value !== null
    ? (value as ChatContextRecord)
    : null;
}

function splitTokens(value: string | null): string[] {
  if (!value) return [];
  return value
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((token) => token.length > 0);
}

function contextTokens(context: ChatContextRecord | null): string[] {
  return [
    context?.selectionSurface,
    context?.selectedElementId,
    context?.selectedBlockName,
    context?.selectedBlockType,
    context?.selectedElementType,
    context?.selectedPropertyKey,
    context?.pageId,
    context?.pageName,
    context?.flowId,
    context?.flowName,
  ].flatMap((value) => splitTokens(readString(value)));
}

function findAnchorRule(tokens: string[]): AnchorKindRule | null {
  return (
    ANCHOR_KIND_RULES.find((rule) =>
      rule.tokens.some((token) => tokens.includes(token)),
    ) ?? null
  );
}

export function labelAgentWorkspaceAnchorFromChatContext(
  chatContext: unknown,
): string {
  const context = readRecord(chatContext);
  const selectedElementLabel = readString(context?.selectedElementLabel);
  const selectedBlockLabel = readString(context?.selectedBlockLabel);
  const selectedElementId = readString(context?.selectedElementId);
  const selectedBlockName = readString(context?.selectedBlockName);
  const selectedPropertyKey = readString(context?.selectedPropertyKey);
  const flowName = readString(context?.flowName);
  const pageName = readString(context?.pageName);
  if (selectedElementLabel) return selectedElementLabel;
  if (selectedBlockLabel) return selectedBlockLabel;
  if (selectedElementId && selectedBlockName && selectedPropertyKey) {
    return `${selectedBlockName} / ${selectedElementId} / ${selectedPropertyKey}`;
  }
  if (selectedElementId && selectedBlockName) {
    return `${selectedBlockName} / ${selectedElementId}`;
  }
  return (
    selectedElementId ??
    selectedBlockName ??
    selectedPropertyKey ??
    flowName ??
    pageName ??
    "workspace"
  );
}

export function resolveAgentWorkspaceAnchorFromChatContext(
  chatContext: unknown,
): AgentWorkspaceAnchor {
  const context = readRecord(chatContext);
  const selectedElementId = readString(context?.selectedElementId);
  const selectedBlockName = readString(context?.selectedBlockName);
  const selectedPropertyKey = readString(context?.selectedPropertyKey);
  const pageId = readString(context?.pageId);
  const pageName = readString(context?.pageName);
  const flowId = readString(context?.flowId);
  const label = labelAgentWorkspaceAnchorFromChatContext(chatContext);
  const rule = findAnchorRule(contextTokens(context));
  const kind =
    rule?.kind ??
    (selectedElementId ? "application.block" : "studio.workspace");
  const targetFormats = rule?.targetFormats ?? ["preview"];
  const reportNodeId =
    kind.startsWith("report.") || kind.startsWith("sheet.")
      ? (selectedElementId ??
        selectedBlockName ??
        selectedPropertyKey ??
        undefined)
      : undefined;

  const anchorId =
    selectedElementId && selectedBlockName
      ? `studio.preview.${selectedBlockName}.${selectedElementId}`
      : selectedElementId
        ? `studio.preview.${selectedElementId}`
        : selectedBlockName
          ? `studio.block.${selectedBlockName}`
          : selectedPropertyKey
            ? `studio.preview.property.${selectedPropertyKey}`
            : "studio.workspace.root";

  return createAgentWorkspaceAnchor({
    anchorId,
    kind,
    label,
    refs: {
      pageId: pageId ?? pageName ?? undefined,
      blockId: selectedBlockName ?? undefined,
      propertyKey: selectedPropertyKey ?? undefined,
      flowIds: flowId ? [flowId] : undefined,
      reportNodeId,
      previewElementId: selectedElementId ?? undefined,
      ruleIds: rule?.ruleIds,
    },
    targetFormats,
    evidenceRefs: [
      "apps/cogo-studio/src/features/preview/selection",
      ...(rule?.evidenceRefs ?? []),
    ],
  });
}
